/**
 * ServiceRequestsList — merchant-facing list of "طلب إدراج خدمة" rows
 * created against their linked companies. Each row shows the request
 * number, company, service names, current stage and amount (when the
 * admin has already set a fee).
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import {
  Loader2, Briefcase, Building2, ArrowRight, Clock, CheckCircle,
  AlertCircle, Receipt,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_META = {
  REQUEST_RECEIVED:  { ar: 'تم استلام الطلب',       en: 'Received',  color: 'sky' },
  REQUEST_ACCEPTED:  { ar: 'تم قبول الطلب',         en: 'Accepted',  color: 'cyan' },
  IN_PROGRESS:       { ar: 'جاري التنفيذ',          en: 'In progress', color: 'amber' },
  FEE_COLLECTION:    { ar: 'تحصيل الرسوم',          en: 'Fee due',   color: 'orange' },
  COMPLETED_AND_DISPATCHED: { ar: 'تم الانتهاء',    en: 'Completed', color: 'emerald' },
};

function stageBadge(stage, language, isDarkMode) {
  const meta = STAGE_META[stage] || { ar: stage, en: stage, color: 'slate' };
  const label = language === 'ar' ? meta.ar : meta.en;
  const cls = {
    sky:    isDarkMode ? 'bg-sky-500/15 text-sky-300'       : 'bg-sky-100 text-sky-700',
    cyan:   isDarkMode ? 'bg-cyan-500/15 text-cyan-300'     : 'bg-cyan-100 text-cyan-700',
    amber:  isDarkMode ? 'bg-amber-500/15 text-amber-300'   : 'bg-amber-100 text-amber-700',
    orange: isDarkMode ? 'bg-orange-500/15 text-orange-300' : 'bg-orange-100 text-orange-700',
    emerald:isDarkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
    slate:  isDarkMode ? 'bg-slate-500/15 text-slate-300'   : 'bg-slate-100 text-slate-700',
  }[meta.color];
  return <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

export default function ServiceRequestsList() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('business_token');
        const res = await axios.get(`${BACKEND_URL}/api/service-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRequests(res.data?.data || []);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === 'string'
          ? detail
          : (language === 'ar' ? 'تعذّر جلب الطلبات' : 'Could not load requests'));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [language]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return requests;
    return requests.filter((r) => (r.stage || r.status) === statusFilter);
  }, [requests, statusFilter]);

  return (
    <div className="min-h-screen cgm-page">
      <Navbar onLogout={() => {
        navigate('/', { replace: true });
        localStorage.removeItem('business_token');
        localStorage.removeItem('business_user');
        window.dispatchEvent(new Event('userLoggedOut'));
      }} />
      <div className="flex">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <main className={`flex-1 min-w-0 transition-all duration-300 pb-10 ${sidebarOpen ? (language === 'ar' ? 'lg:mr-64' : 'lg:ml-64') : ''}`}>
          {/* Hero */}
          <section className="cgm-hero py-6 sm:py-8 mb-6">
            <div className="cgm-hero-content max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="cgm-reveal">
                <div className="flex items-center gap-3 mb-2">
                  <div className="cgm-icon-glow cgm-breathe !p-2.5">
                    <Briefcase className="h-5 w-5 text-white" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                    {language === 'ar' ? 'طلبات الخدمات' : 'Service Requests'}
                  </h1>
                </div>
                <p className="text-white/80 text-sm sm:text-base">
                  {language === 'ar'
                    ? 'كل طلبات إدراج الخدمات التي أرسلتها من ملفات شركاتك.'
                    : 'All service-listing requests you have sent from your company files.'}
                </p>
              </div>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Status filter chips */}
            <div className="flex gap-2 mb-5 flex-wrap">
              {[
                { id: 'all', label_ar: 'الكل', label_en: 'All' },
                { id: 'REQUEST_RECEIVED', label_ar: 'مستلمة', label_en: 'Received' },
                { id: 'REQUEST_ACCEPTED', label_ar: 'مقبولة', label_en: 'Accepted' },
                { id: 'IN_PROGRESS', label_ar: 'جاري', label_en: 'In progress' },
                { id: 'FEE_COLLECTION', label_ar: 'تحصيل الرسوم', label_en: 'Fee due' },
                { id: 'COMPLETED_AND_DISPATCHED', label_ar: 'مكتملة', label_en: 'Completed' },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStatusFilter(s.id)}
                  className={statusFilter === s.id ? 'cgm-chip-active' : 'cgm-chip'}
                  data-testid={`sr-filter-${s.id.toLowerCase()}`}
                >
                  {language === 'ar' ? s.label_ar : s.label_en}
                </button>
              ))}
            </div>

            {loading && (
              <div className="cgm-ai-card">
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#38BDF8]" />
                </div>
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="cgm-ai-card text-center !py-16" data-testid="service-requests-empty">
                <div className="cgm-icon-glow !w-16 !h-16 !p-4 inline-flex mb-4 cgm-float">
                  <Briefcase className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-black text-lg mb-1 cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? 'لا توجد طلبات' : 'No service requests yet'}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar'
                    ? 'افتح ملف شركتك واضغط «طلب إدراج خدمة» لإنشاء طلب جديد.'
                    : 'Open your company file and tap “Request a Service” to create one.'}
                </p>
              </div>
            )}

            {!loading && filtered.length > 0 && (
              <div className="space-y-3 cgm-reveal-stagger">
                {filtered.map((r) => {
                  const stage = r.stage || r.status || 'REQUEST_RECEIVED';
                  const hasFee = Number(r.payment?.total || r.payment?.subtotal || 0) > 0;
                  return (
                    <button
                      type="button"
                      key={r.id}
                      onClick={() => navigate(`/service-requests/${r.id}`)}
                      className="cgm-ai-card-interactive text-start w-full !p-0 group"
                      data-testid={`sr-row-${r.id}`}
                    >
                      <div className="p-4 sm:p-5">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className="cgm-icon-glow !p-2 !w-10 !h-10 flex-shrink-0 group-hover:scale-110 transition-transform duration-500">
                              <Briefcase className="h-4 w-4 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`font-mono text-sm font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid={`sr-no-${r.id}`}>
                                  {r.request_no || r.id?.slice(0, 10)}
                                </span>
                                {stageBadge(stage, language, isDarkMode)}
                              </div>
                              <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {language === 'ar'
                                  ? (r.main_service_name_ar || r.main_service_name_en)
                                  : (r.main_service_name_en || r.main_service_name_ar)}
                                {' › '}
                                <span className="font-semibold">
                                  {language === 'ar'
                                    ? (r.sub_service_name_ar || r.sub_service_name_en)
                                    : (r.sub_service_name_en || r.sub_service_name_ar)}
                                </span>
                              </p>
                              <div className={`flex items-center gap-2 mt-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                <Building2 className="h-3.5 w-3.5" />
                                <span className="truncate">
                                  {language === 'ar'
                                    ? (r.company_name_ar || r.company_name_en)
                                    : (r.company_name_en || r.company_name_ar)}
                                </span>
                                <span className="opacity-50">•</span>
                                <Clock className="h-3.5 w-3.5" />
                                <span>
                                  {new Date(r.created_at || r.createdAt || Date.now()).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
                                    year: 'numeric', month: 'short', day: 'numeric',
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                            {hasFee ? (
                              <>
                                <span className="cgm-stat-value-ai !text-xl">
                                  {Number(r.payment?.total || 0).toFixed(2)} AED
                                </span>
                                {stage === 'FEE_COLLECTION' && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-500">
                                    <Receipt className="h-3 w-3" />
                                    {language === 'ar' ? 'بانتظار الدفع' : 'Awaiting payment'}
                                  </span>
                                )}
                                {stage === 'COMPLETED_AND_DISPATCHED' && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-500">
                                    <CheckCircle className="h-3 w-3" />
                                    {language === 'ar' ? 'مدفوع ومكتمل' : 'Paid & done'}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className={`text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                {language === 'ar' ? 'بانتظار تحديد الرسوم' : 'Fee pending'}
                              </span>
                            )}
                            <ArrowRight className="h-4 w-4 rtl:rotate-180 opacity-50 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
